// frontend/src/components/Chat.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, TextField, Button, IconButton, Menu, MenuItem, Avatar, Paper, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Settings as SettingsIcon, ArrowBackIos as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentIdentity } from '../userIdentity';

const API_BASE = '/api';

const Chat = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();

  const [bot, setBot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [active, setActive] = useState(true);
  const [userId, setUserId] = useState(null);
  const [userAvatar, setUserAvatar] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [chatDetails, setChatDetails] = useState(null); 
  const [isCreator, setIsCreator] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const scrollRef = useRef(null);
  
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);

  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      const initData = window.Telegram.WebApp.initDataUnsafe;
      window.Telegram.WebApp.BackButton.show();
      window.Telegram.WebApp.BackButton.onClick(() => navigate('/chats'));

      getCurrentIdentity().then((identity) => {
        setUserId(identity.userId);
      });
    }

    return () => {
      if (window.Telegram && window.Telegram.WebApp) {
        window.Telegram.WebApp.BackButton.hide();
      }
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [navigate]);

  useEffect(() => {
    let interval = null;

    const initChat = async () => {
      const identity = await getCurrentIdentity();
      let currentId = null;
      if (identity?.userId) {
        currentId = String(identity.userId);
        setUserId(currentId);
      }
      if (identity?.avatar) {
        setUserAvatar(identity.avatar);
      }

      await fetchChat();

      interval = setInterval(() => {
        fetchChat();
      }, 3000);
    };

    initChat();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [chatId]);

  useEffect(() => {
    if (isAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAutoScroll]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setIsAutoScroll(atBottom);
  };


  const fetchChat = async () => {
    try {
      const res = await fetch(`${API_BASE}/chats/details/${chatId}`);
      if (!res.ok) throw new Error('Ошибка');
      const data = await res.json();
      setBot(data.bot);
      setMessages(data.messages || []);
      setActive(data.active !== false);
      setChatDetails(data.chat || null);
      setLoading(false);
    } catch (err) {
      console.error('Ошибка:', err);
      setLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm("Покинуть группу?")) return;
    try {
      const res = await fetch(`${API_BASE}/groups/${chatId}/leave`, { method: 'POST', body: JSON.stringify({ userId }) });
      if (res.ok) navigate('/chats');
    } catch (error) { console.error(error); }
  };

  const handleLeaveAndDeleteGroup = async () => {
    if (!window.confirm("Выйти и удалить группу навсегда?")) return;
    try {
      const res = await fetch(`${API_BASE}/groups/${chatId}`, { method: 'DELETE' });
      if (res.ok) navigate('/chats');
    } catch (error) { console.error(error); }
  };

  const fetchChatMessagesOnly = async () => {
    try {
      const response = await fetch(`${API_BASE}/chats/details/${chatId}`);
      if (response.ok) {
        const data = await response.json();

        setMessages((prev) => {
          if (
            prev.length !== data.messages.length ||
            (prev.length > 0 &&
              data.messages.length > 0 &&
              prev[prev.length - 1]?.text !== data.messages[data.messages.length - 1]?.text)
          ) {
            return data.messages || [];
          }
          return prev;
        });

        setActive(data.active ?? true);
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  };

  const fetchUserAvatar = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE}/profile/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserAvatar(data.avatar || '');
      }
    } catch (error) {
      console.error('Error fetching user avatar:', error);
    }
  };

  const addMessage = (message) => {
    setMessages((prev) => [
      ...prev,
      {
        ...message,
        timestamp: new Date().toISOString(),
      },
    ]);
  };

  const saveMessage = async (payload) => {
    const response = await fetch(`${API_BASE}/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  };

  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;

    addMessage({
      text,
      sender: 'user',
      type: 'text',
      userId
    });

    setInput('');

    try {
      const data = await saveMessage({
        text,
        sender: 'user',
        type: 'text',
        userId
      });

      if (data.botResponse) {
        addMessage({
          text: data.botResponse,
          sender: 'bot',
          type: 'text',
        });
      }
    } catch (error) {
      fetchChat();
    }
  };

  const handlePhotoSelect = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) return;

    const imageUrl = await fileToDataUrl(file);
    const caption = input.trim();

    addMessage({
      text: caption ? `[Фото] ${caption}` : '[Фото]',
      caption,
      imageUrl,
      sender: 'user',
      type: 'photo',
      userId
    });

    setInput('');

    try {
      const data = await saveMessage({
        sender: 'user',
        type: 'photo',
        imageUrl,
        caption,
        userId
      });

      if (data.botResponse) {
        addMessage({
          text: data.botResponse,
          sender: 'bot',
          type: 'text',
        });
      }
    } catch (error) {
      fetchChat();
    }
  };

  const handleConfirmDeleteChat = async () => {
    try {
      const response = await fetch(`${API_BASE}/chats/${chatId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        navigate('/chats');
      } else {
        alert('Не');
      }
    } catch (error) {
      console.error('Ошибка:', error);
      alert('Ошибка');
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleConfirmResetMemory = async () => {
    try {
      const response = await fetch(`${API_BASE}/chats/${chatId}/reset`, {
        method: 'POST',
      });
      if (response.ok) {
        setMessages([]); 
        setAnchorEl(null);
      } else {
        alert('Не');
      }
    } catch (error) {
      console.error('Ошибка:', error);
    } finally {
      setOpenResetDialog(false);
    }
  };

  const handleRewind = async () => {
    try {
      const response = await fetch(`${API_BASE}/chats/${chatId}/rewind-init`, {
        method: 'POST',
      });
      if (response.ok) {
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.close();
        }
      } else {
        alert('Не удалось отмотать');
      }
    } catch (error) {
      console.error('Ошибка отмотки:', error);
    }
  };

  const handleOpenTelegram = async () => {
    try {
      const response = await fetch(`${API_BASE}/chats/${chatId}/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ userId }) 
      });

      if (response.ok) {
        setActive(true);

        const botUsername = 'Immaginarium_Bot';

        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.openTelegramLink(`https://t.me/${botUsername}`);
        }
      }
    } catch (error) {
      console.error('Error opening Telegram chat:', error);
    }
  };

  const renderedMessages = useMemo(() => {
    return messages.map((msg, index) => {
      const isGroupChat = Boolean(chatDetails?.isGroup === 1 || chatDetails?.isGroup === true);
      const msgUserIdStr = msg.userId ? String(msg.userId) : null;
      const currentUserIdStr = userId ? String(userId) : null;

      const isMe = msg.sender === 'user' && (
        !isGroupChat || 
        (msgUserIdStr && currentUserIdStr && msgUserIdStr === currentUserIdStr)
      );

      const isOtherUser = msg.sender === 'user' && !isMe;
      const isBot = msg.sender === 'bot';
      const isSystem = msg.sender === 'system';

      let msgDisplayName = '';
      if (isOtherUser) {
        msgDisplayName = msg.dndName || msg.userNick || 'Игрок';
      } else if (isBot) {
        msgDisplayName = (chatDetails?.isDndMode || bot?.isDndMode) ? 'Мастер' : (bot?.name || 'Бот');
      } else if (isMe) {
        msgDisplayName = msg.dndName || 'Вы';
      }

      let currentAvatar = '';
      if (isMe) {
        currentAvatar = userAvatar || msg.userAvatar || '';
      } else if (isOtherUser) {
        currentAvatar = msg.userAvatar || '';
      } else if (isBot) {
        currentAvatar = bot?.avatar || '';
      } else if (isSystem) {
        currentAvatar = chatDetails?.groupAvatar || '';
      }

      const avatarLetter = (msgDisplayName || (isMe ? 'Я' : 'U'))[0].toUpperCase();

      return (
        <Box
          key={msg.id || index}
          sx={{
            display: 'flex',
            justifyContent: isMe ? 'flex-end' : 'flex-start',
            alignItems: 'flex-end',
            mb: 1.5,
          }}
        >
          {!isMe && (
            <Avatar 
              src={currentAvatar || undefined} 
              sx={{ 
                width: 34, height: 34, mr: 1, mb: 0.5,
                bgcolor: isBot ? '#8e24aa' : '#555',
                fontSize: '0.85rem', fontWeight: 'bold'
              }} 
            >
              {!currentAvatar && avatarLetter}
            </Avatar>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
            {!isMe && msgDisplayName && !isSystem && (
              <Typography variant="caption" sx={{ color: isBot ? '#ce93d8' : '#aaa', ml: 1, mb: 0.3, fontSize: '0.75rem', fontWeight: 'bold' }}>
                {msgDisplayName}
              </Typography>
            )}

            <Box
              sx={{
                p: 1.5,
                borderRadius: isMe ? '18px 18px 0 18px' : '18px 18px 18px 0',
                backgroundColor: isMe 
                  ? '#007AFF' 
                  : (isOtherUser 
                      ? '#3a3a3a' 
                      : (isBot ? ((chatDetails?.isDndMode || bot?.isDndMode) ? '#2c243b' : '#2a2a2a') : '#222')),
                color: 'white',
                wordBreak: 'break-word',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                border: isBot && (chatDetails?.isDndMode || bot?.isDndMode) ? '1px solid #7b1fa2' : 'none'
              }}
            >
              {msg.type === 'photo' ? (
                <Box>
                  {!!msg.imageUrl && (
                    <Box component="img" src={msg.imageUrl} alt="photo" sx={{ display: 'block', width: '100%', maxWidth: '320px', borderRadius: '12px', mb: msg.caption ? 1 : 0 }} />
                  )}
                  {!!msg.caption && <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>{msg.caption}</Typography>}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                  {msg.text}
                </Typography>
              )}
            </Box>
          </Box>

          {isMe && (
            <Avatar 
              src={currentAvatar || undefined} 
              sx={{ 
                width: 34, height: 34, ml: 1, mb: 0.5,
                bgcolor: '#007AFF',
                fontSize: '0.85rem', fontWeight: 'bold'
              }} 
            >
              {!currentAvatar && avatarLetter}
            </Avatar>
          )}
        </Box>
      );
    });
  }, [messages, chatDetails, bot, userId, userAvatar]);

  if (loading) return <Box sx={{ p: 2, color: 'white' }}>Загрузка чата...</Box>;
  if (!bot) return <Box sx={{ p: 2, color: 'white' }}>Чат не найден</Box>;

  const isGroup = chatDetails?.isGroup === 1;
  const displayAvatar = isGroup ? chatDetails.groupAvatar : bot.avatar;
  const displayName = isGroup ? chatDetails.groupName : bot.name;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#121212' }}>
      <Paper
        elevation={0}
        sx={{
          p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: '#1e1e1e', borderBottom: '1px solid #333', zIndex: 10,
        }}
      >
        <IconButton onClick={() => navigate('/chats')} sx={{ color: '#fff' }}>
          <ArrowBackIcon />
        </IconButton>

        <Box
          sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', cursor: 'pointer' }}
          onClick={() => isGroup ? navigate(`/group/${chatId}`) : navigate(`/bot/${bot.botRealId}`)}
        >
          <Avatar src={displayAvatar} sx={{ width: 40, height: 40, mr: 1, border: '1px solid #555' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff', fontSize: '1rem', lineHeight: 1 }}>
              {displayName}
            </Typography>
            {isGroup && (
              <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic', fontSize: '0.75rem' }}>
                {chatDetails.inviteCode}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: '#fff' }}>
          <SettingsIcon />
        </IconButton>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          {isGroup ? (
            isCreator ? (
              <MenuItem onClick={() => { handleLeaveAndDeleteGroup(); setAnchorEl(null); }} sx={{ color: 'red' }}>
                Выйти и удалить группу
              </MenuItem>
            ) : (
              <MenuItem onClick={() => { handleLeaveGroup(); setAnchorEl(null); }} sx={{ color: 'red' }}>
                Покинуть группу
              </MenuItem>
            )
          ) : (
            <MenuItem onClick={() => { setOpenDeleteDialog(true); setAnchorEl(null); }} sx={{ color: 'red' }}>
              Удалить чат
            </MenuItem>
          )}
          
          <MenuItem onClick={() => { setOpenResetDialog(true); setAnchorEl(null); }}>Сбросить память</MenuItem>
          <MenuItem onClick={() => { handleRewind(); setAnchorEl(null); }}>Отмотать память</MenuItem>
        </Menu>
      </Paper>

      <Box 
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {renderedMessages}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ p: 2, backgroundColor: '#1e1e1e', borderTop: '1px solid #333' }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenTelegram}
          sx={{
            borderRadius: '16px',
            mb: 2,
            backgroundColor: '#2a2a2a',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#333' },
          }}
        >
          Выбрать
        </Button>

        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            style={{ display: 'none' }}
          />

          <Button
            variant="contained"
            onClick={handlePhotoSelect}
            sx={{
              minWidth: '44px',
              width: '44px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#2a2a2a',
              color: '#fff',
              '&:hover': { backgroundColor: '#3a3a3a' },
            }}
          >
            ＋
          </Button>

          <TextField
            variant="outlined"
            fullWidth
            multiline
            maxRows={5}
            placeholder="Сообщение..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            size="small"
            sx={{
              backgroundColor: '#2a2a2a',
              borderRadius: '20px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                color: 'white',
                padding: '10px 14px',
                '& fieldset': { border: 'none' },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            sx={{
              borderRadius: '50%',
              minWidth: '50px',
              width: '50px',
              height: '40px',
              backgroundColor: '#007AFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              '&:hover': { backgroundColor: '#005BB5' },
            }}
          >
            ➤
          </Button>
        </Box>
      </Box>

      {/* Модалки подтверждения */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{ sx: { backgroundColor: '#1e1e1e', color: 'white', borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Удаление чата</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#aaa' }}>
            Вы действительно хотите удалить чат?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#aaa' }}>
            Нет
          </Button>
          <Button onClick={handleConfirmDeleteChat} sx={{ color: '#ff4444', fontWeight: 'bold' }}>
            Да
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResetDialog}
        onClose={() => setOpenResetDialog(false)}
        PaperProps={{ sx: { backgroundColor: '#1e1e1e', color: 'white', borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Сброс памяти</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#aaa' }}>
            Вы действительно хотите стереть ВСЮ память?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenResetDialog(false)} sx={{ color: '#aaa' }}>
            Нет
          </Button>
          <Button onClick={handleConfirmResetMemory} sx={{ color: '#ff4444', fontWeight: 'bold' }}>
            Да
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Chat;